import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Upload, ShieldCheck, X, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useBackgroundJobs } from "@/lib/backgroundJobs";

// Fixed bottom-right indicator that shows any tracked upload/verify jobs
// while they run — so users can navigate away and still watch progress.
export default function BackgroundJobsIndicator() {
  const { jobs, dismiss } = useBackgroundJobs();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  const visible = jobs.filter((j) => j.status !== "dismissed");
  if (visible.length === 0) return null;

  const running = visible.filter((j) => !isTerminal(j.status));
  const done = visible.filter((j) => isTerminal(j.status));

  return (
    <div
      data-testid="bg-jobs-indicator"
      className="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)]"
    >
      <div className="border border-slate-200 bg-white rounded-md shadow-lg overflow-hidden">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs"
          data-testid="bg-jobs-toggle"
        >
          <span className="flex items-center gap-2 font-semibold text-slate-800">
            {running.length > 0 && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
            Background jobs
            <span className="text-slate-500 font-mono">
              {running.length > 0 ? `${running.length} running` : `${done.length} done`}
            </span>
          </span>
          {collapsed ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </button>
        {!collapsed && (
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {visible.map((j) => (
              <JobRow key={j.id} job={j} onClick={() => navigate(routeFor(j))} onDismiss={() => dismiss(j.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, onClick, onDismiss }) {
  const pct = job.total ? Math.min(100, Math.round((100 * (job.processed || 0)) / job.total)) : 0;
  const terminal = isTerminal(job.status);
  const failed = job.status === "failed" || job.status === "error";
  const Icon = job.kind === "verification" || job.kind === "leads_verify" ? ShieldCheck : Upload;

  return (
    <div className="p-3 text-xs" data-testid={`bg-job-${job.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <button onClick={onClick} className="flex items-start gap-2 flex-1 text-left hover:text-slate-900 text-slate-700 min-w-0">
          <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold truncate" title={job.title}>{job.title || labelFor(job.kind)}</div>
            <div className="text-[10px] font-mono text-slate-500">
              {terminal
                ? failed ? "Failed" : `Complete · ${job.total.toLocaleString()} rows`
                : `${(job.processed || 0).toLocaleString()} / ${job.total.toLocaleString()}${job.rate_per_sec ? ` · ${job.rate_per_sec}/s` : ""}${job.eta_seconds ? ` · ETA ${fmtSec(job.eta_seconds)}` : ""}`}
            </div>
          </div>
        </button>
        <button onClick={onDismiss} className="text-slate-400 hover:text-slate-700 p-1" title="Dismiss" data-testid={`bg-job-dismiss-${job.id}`}>
          {terminal ? <X className="w-3 h-3" /> : <X className="w-3 h-3" />}
        </button>
      </div>
      <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${failed ? "bg-rose-500" : terminal ? "bg-emerald-500" : "bg-sky-500"}`}
          style={{ width: `${terminal ? 100 : pct}%` }}
        />
      </div>
      {terminal && failed && (
        <div className="text-[10px] font-mono text-rose-600 mt-1 flex items-center gap-1">
          <XCircle className="w-3 h-3" /> Check the source page for details
        </div>
      )}
      {terminal && !failed && job.summary && (
        <div className="text-[10px] font-mono text-slate-500 mt-1 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-600" /> {compactSummary(job.summary)}
        </div>
      )}
    </div>
  );
}

function labelFor(kind) {
  return kind === "verification" ? "Email verification" :
    kind === "leads_upload" ? "Leads upload" :
      kind === "leads_verify" ? "Leads verification" : "Job";
}

function routeFor(job) {
  if (job.kind === "verification") return "/app/email-database?tab=verification";
  return "/app/email-database?tab=leads";
}

function isTerminal(status) {
  return status === "complete" || status === "completed" || status === "failed" || status === "error" || status === "cancelled";
}

function fmtSec(s) {
  if (s == null) return "";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

function compactSummary(sum) {
  if (!sum) return "";
  if (sum.deliverable != null) {
    return `${sum.deliverable.toLocaleString()} deliverable · ${(sum.undeliverable || 0).toLocaleString()} bad`;
  }
  if (sum.inserted != null) {
    return `${sum.inserted.toLocaleString()} inserted · ${(sum.skipped || 0).toLocaleString()} skipped`;
  }
  return "";
}
